import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Section } from '../../../components/ui/Section';

export default function GoogleConfigSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    googleClientId: '', googleClientSecret: '', googleEnabled: false,
  });
  const [loaded, setLoaded] = useState(false);
  const [secretSet, setSecretSet] = useState(false);

  const { data } = useQuery({ queryKey: ['system-google'], queryFn: () => apiGet<any>('/system/google') });

  useEffect(() => {
    if (data && !loaded) {
      setForm({ googleClientId: data.googleClientId ?? '', googleClientSecret: '', googleEnabled: data.googleEnabled ?? false });
      setSecretSet(data.googleSecretSet);
      setLoaded(true);
    }
  }, [data, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => apiPatch('/system/google', {
      ...(form.googleClientId ? { googleClientId: form.googleClientId } : {}),
      ...(form.googleClientSecret ? { googleClientSecret: form.googleClientSecret } : {}),
      googleEnabled: form.googleEnabled,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['system-google'] }); toast.success('Config Google salvata'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const sourceColor = data?.source === 'db' ? 'var(--color-success)' : data?.source === 'env' ? 'var(--color-warning)' : 'var(--color-text-muted)';
  const sourceLabel = data?.source === 'db' ? 'Da DB' : data?.source === 'env' ? 'Da .env' : 'Nessuno';
  const credentialsMissing = form.googleEnabled && (!form.googleClientId || (!secretSet && !form.googleClientSecret));

  return (
    <Section title="Configurazione Google OAuth2" collapsible defaultOpen={false}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ padding: '0.75rem', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-card)', borderLeft: '4px solid var(--color-warning)' }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text)', margin: 0 }}>⚠️ Modifiche applicate immediatamente senza riavvio server.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Origine:</span>
          <span style={{ fontSize: 'var(--text-xs)', color: sourceColor, fontWeight: 600 }}>{sourceLabel}</span>
        </div>
        <Input placeholder="xxxx.apps.googleusercontent.com" value={form.googleClientId} onChange={e => setForm({ ...form, googleClientId: e.target.value })} autoComplete="off" />
        <Input type="password" placeholder={secretSet ? '••••••••' : 'Client Secret'} value={form.googleClientSecret} onChange={e => setForm({ ...form, googleClientSecret: e.target.value })} autoComplete="new-password" />
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', padding: '0.75rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)' }}>
          <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Redirect URI (auto-rilevati):</p>
          <p style={{ margin: '0 0 0.25rem' }}>SSO:</p>
          <code style={{ fontSize: '0.75rem', background: 'var(--glass-bg-card)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-xs)', wordBreak: 'break-all' }}>
            {typeof window !== 'undefined' ? `${window.location.origin}/api/auth/google/callback` : '/api/auth/google/callback'}
          </code>
          <p style={{ margin: '0.5rem 0 0.25rem' }}>Calendar:</p>
          <code style={{ fontSize: '0.75rem', background: 'var(--glass-bg-card)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-xs)', wordBreak: 'break-all' }}>
            {typeof window !== 'undefined' ? `${window.location.origin}/api/calendar/callback` : '/api/calendar/callback'}
          </code>
          <p style={{ margin: '0.5rem 0 0.25rem' }}>Calendar personale:</p>
          <code style={{ fontSize: '0.75rem', background: 'var(--glass-bg-card)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-xs)', wordBreak: 'break-all' }}>
            {typeof window !== 'undefined' ? `${window.location.origin}/api/calendar/personal/callback` : '/api/calendar/personal/callback'}
          </code>
          <p style={{ margin: '0.5rem 0 0' }}>Aggiungi tutti gli URI nella Google Cloud Console</p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
          <input type="checkbox" checked={form.googleEnabled} onChange={e => setForm({ ...form, googleEnabled: e.target.checked })} />
          Abilita Google OAuth2
        </label>
        {credentialsMissing && (
          <div style={{ padding: '0.75rem', background: 'var(--color-error-bg)', borderRadius: 'var(--radius-card)', borderLeft: '4px solid var(--color-error)' }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)', margin: 0 }}>❌ Client ID e Secret obbligatori per abilitare</p>
          </div>
        )}
        <Button variant="primary" onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={credentialsMissing}>
          Salva configurazione Google
        </Button>
      </div>
    </Section>
  );
}
